import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
  Grid,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// Utils
import messages from '../../../static/messages';

// Services
import proposalsServices from '../../../services/proposalsServices';
import warrantyServices from '../../../services/warrantyServices';

// Drag and Drop
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

// Auto-save
import useAutoSave from '../../../hooks/useAutoSave';
import AutoSaveIndicator from '../../AutoSaveIndicator/AutoSaveIndicator';

// DnD Components
const DragHandle = SortableHandle(() => (
  <DragIndicatorIcon sx={{ cursor: 'grab', color: '#999', '&:hover': { color: '#333' } }} />
));

const SortableTableRow = SortableElement(({ children, ...props }) => (
  <TableRow {...props}>{children}</TableRow>
));

const SortableTableBody = SortableContainer(({ children }) => (
  <TableBody>{children}</TableBody>
));

const ProposalWarrantySection = ({ proposal, id, refresh }) => {
  const [warranties, setWarranties] = useState([]);
  const [warrantyList, setWarrantyList] = useState([]);
  const [adding, setAdding] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const dirtyRef = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const confirm = useConfirm();

  // Auto-save function
  const saveWarranties = useCallback(async (data) => {
    const response = await proposalsServices.update(id, { warranties: data });
    dirtyRef.current = false;
    if (response.status === 200 && refresh) {
      refresh();
    }
  }, [id, refresh]);

  // Save immediately on user actions (add/delete/reorder) — no timer
  const {
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    error,
    saveNow,
    getLastSavedText,
  } = useAutoSave(saveWarranties);

  const saveNowSafe = useCallback((data) => {
    dirtyRef.current = true;
    saveNow(data).catch(() => {
      toast.error('Auto-save failed. Please retry the action.');
    });
  }, [saveNow]);

  // Load available warranty items
  const loadWarrantyList = async () => {
    try {
      const response = await warrantyServices.getAllWarranty(0, 1000000, false);
      if (response.status === 206 || response.status === 200) {
        setWarrantyList(response.data.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Handler functions
  const handleAdd = () => {
    if (!selectedItem) {
      toast.error('Please select a warranty item');
      return;
    }

    const item = warrantyList.find((w) => w.id === selectedItem);
    if (!item) return;

    // Check if already added
    if (warranties.some((w) => w.id === item.id)) {
      toast.warning('This warranty is already added');
      return;
    }

    const newItem = {
      id: item.id,
      description: item.description,
      years: item.years || '',
      price: Number(item.price) || 0,
    };

    const updated = [...warranties, newItem];
    setWarranties(updated);
    saveNowSafe(updated);
    setAdding(false);
    setSelectedItem('');
  };

  const handleDelete = (itemId) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('this warranty'),
    })
      .then(() => {
        const updated = warranties.filter((w) => w.id !== itemId);
        setWarranties(updated);
        saveNowSafe(updated);
      })
      .catch(() => toast.info(messages.cancel_message));
  };

  // Handle drag and drop reorder
  const handleSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const updated = arrayMoveImmutable(warranties, oldIndex, newIndex);
    setWarranties(updated);
    saveNowSafe(updated);
  };

  // Calculate total
  const totalPrice = warranties.reduce((sum, w) => sum + (Number(w.price) || 0), 0);

  // Hooks
  useEffect(() => {
    loadWarrantyList();
  }, []);

  useEffect(() => {
    if (proposal?.warranties && !dirtyRef.current) {
      setWarranties(proposal.warranties);
    }
  }, [proposal]);

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0,
        mb: 2 
      }}>
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>
          Warranty
          <Typography component="span" sx={{ ml: 2, color: 'primary.main', fontWeight: 600, fontSize: isMobile ? '0.875rem' : 'inherit' }}>
            Total: ${totalPrice.toFixed(2)}
          </Typography>
        </Typography>
        <AutoSaveIndicator
            isSaving={isSaving}
            hasUnsavedChanges={hasUnsavedChanges}
            lastSaved={lastSaved}
            error={error}
            getLastSavedText={getLastSavedText}
            variant="chip"
          />
      </Box>
      {warranties.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No warranties added. Click "Add Warranty" to add items from the catalog.
        </Typography>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: isMobile ? 'unset' : 500 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ width: isMobile ? 30 : 40, p: isMobile ? '4px' : undefined }}></TableCell>
                  <TableCell>Description</TableCell>
                  {!isMobile && <TableCell sx={{ width: 100 }}>Years</TableCell>}
                  <TableCell align="right" sx={{ width: isMobile ? 70 : 120 }}>Price</TableCell>
                  <TableCell align="center" sx={{ width: isMobile ? 40 : 60 }}></TableCell>
                </TableRow>
              </TableHead>
              <SortableTableBody useDragHandle onSortEnd={handleSortEnd} helperClass="sortable-row-helper">
                {warranties.map((item, index) => (
                  <SortableTableRow key={`warranty-${item.id}`} index={index}>
                    <TableCell sx={{ width: isMobile ? 30 : 40, p: isMobile ? '4px' : undefined }}>
                      <DragHandle />
                    </TableCell>
                    <TableCell>
                      {item.description}
                      {isMobile && item.years && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {item.years} years
                        </Typography>
                      )}
                    </TableCell>
                    {!isMobile && <TableCell>{item.years}</TableCell>}
                    <TableCell align="right" sx={{ fontSize: isMobile ? '0.8rem' : 'inherit' }}>
                      ${parseFloat(item.price || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="center" sx={{ p: isMobile ? '2px' : undefined }}>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(item.id)} color="error">
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </SortableTableRow>
                ))}
              </SortableTableBody>
            </Table>
          </TableContainer>

          {/* Total Row */}
          <Box sx={{ mt: 1, p: 1, backgroundColor: '#e3f2fd', borderRadius: 1, textAlign: 'right' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Warranty Total: ${totalPrice.toFixed(2)}
            </Typography>
          </Box>
        </>
      )}

      {/* Add new item */}
      {adding && (
        <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: isMobile ? 'stretch' : 'center', 
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap' 
          }}>
            <FormControl size="small" sx={{ minWidth: isMobile ? 'unset' : 400, width: isMobile ? '100%' : 'auto' }}>
              <InputLabel>Select Warranty</InputLabel>
              <Select
                value={selectedItem}
                onChange={(e) => setSelectedItem(e.target.value)}
                label="Select Warranty"
                MenuProps={{
                  PaperProps: {
                    sx: { maxHeight: 300 }
                  }
                }}
              >
                <MenuItem value="" sx={{ fontSize: isMobile ? '0.8rem' : 'inherit' }}>Select from list</MenuItem>
                {warrantyList.map((w) => (
                  <MenuItem
                    key={w.id}
                    value={w.id}
                    disabled={warranties.some((sw) => sw.id === w.id)}
                    sx={{ 
                      whiteSpace: 'normal', 
                      fontSize: isMobile ? '0.8rem' : 'inherit',
                      py: isMobile ? 1 : 0.75
                    }}
                  >
                    {w.description} - {w.years} years (${w.price})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={1} sx={{ width: isMobile ? '100%' : 'auto' }}>
              <Grid item xs={6} sm="auto" sx={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton size="small" color="primary" onClick={handleAdd}>
                  <SaveIcon />
                </IconButton>
              </Grid>
              <Grid item xs={6} sm="auto" sx={{ display: 'flex', justifyContent: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setAdding(false);
                    setSelectedItem('');
                  }}
                >
                  <CancelIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}


      {/* Add button at bottom */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setAdding(true)}
        sx={{ mt: 2 }}
      >
        Add Warranty
      </Button>
    </Box>
  );
};

export default ProposalWarrantySection;
